import classNames from 'classnames';
import { Portal, svgSprite } from '../../hybrid/wrappers';
import css from './CreateEvent.module.scss';
import T from '../../common/components/T';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import UserContext, { UserProfileContext } from '../../common/utils/getContext';
import FormComponent from '../../widgets/form/FormComponent/FormComponent';
import {
    getDefaultData,
    getFormButtons,
    getFormFields,
    getFormFuncs,
    getFormStyles,
    sendFunction,
    setFormDataMainFunc
} from './helpers';
import { getData } from '../../common/utils/router';
import { useWindowDimensions } from '../../common/utils/windowDimensions';

export default function CreateEvent(props) {
    const { closeHandler, data, events, onSuccess } = props;
    const { lang, userToken } = useContext(UserContext);
    const { roles } = useContext(UserProfileContext);
    const [allRoles, setAllRoles] = useState([]);

    useEffect(() => {
        const hasAccessRoles = ['admin', 'admin2', 'superadmin'];
        if (roles && roles?.find((role) => hasAccessRoles?.includes(role?.name))) {
            getData('/api/v1/admin/user-management/roles', userToken, lang).then((res) => setAllRoles(res?.data));
        }
    }, [userToken, lang, roles]);

    const normalizedDateFirst = data?.date.toLocaleString(lang, { month: 'long', day: 'numeric' });
    const normalizedDateSecond = `(${data?.date
        .toLocaleString(lang, { weekday: 'long', year: 'numeric' })
        ?.split(' ')
        ?.reverse()
        ?.join(', ')})`;

    const isMobile = useWindowDimensions() < 768;

    const [formData, setFormData] = useState(getDefaultData(data, events));

    const formFields = useMemo(() => getFormFields(formData, events, allRoles, isMobile), [formData, events, allRoles, isMobile]);
    const formButtons = useMemo(() => getFormButtons(closeHandler, data), [closeHandler, data]);
    const formFuncs = useMemo(() => getFormFuncs(), []);
    const formStyles = useMemo(() => getFormStyles(css), []);

    const sender = useCallback(
        (dataToSend, setAllowedSend, setFieldsErr, setRequestLoading) => {
            sendFunction(
                dataToSend,
                setAllowedSend,
                setFieldsErr,
                userToken,
                lang,
                closeHandler,
                formFuncs,
                onSuccess,
                setRequestLoading
            );
        },
        [userToken, lang, closeHandler, formFuncs, onSuccess]
    );

    useEffect(() => {
        if (isMobile) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobile]);

    return (
        <Portal
            customStyle={{ wrapModalStyle: { marginRight: '-4px' } }}
            unscrollable={isMobile ? false : true}
            closeHandler={closeHandler}
        >
            <div className={css.modal}>
                <div className={css.head}>
                    <div onClick={closeHandler} className={classNames('circleButton', css.modalClose)}>
                        {svgSprite('cross', { className: css.modalCloseSvg })}
                    </div>
                    <p className={css.title}>
                        {data?.id ? (
                            <T textName='edit-event' defaultStr='Редактировать событие' />
                        ) : (
                            <T textName='add-new-event' defaultStr='Добавить новое событие' />
                        )}
                    </p>
                    <p className={css.date}>
                        <span>{normalizedDateFirst}</span>{' '}
                        <span style={{ textTransform: 'capitalize' }}>{normalizedDateSecond}</span>
                    </p>
                </div>
                <div className={css.body}>
                    <FormComponent
                        data={formFields}
                        sendData={{ sender }}
                        formDataMain={formData}
                        setFormDataMain={(key, val) => setFormDataMainFunc(key, val, setFormData)}
                        buttons={formButtons}
                        formStyles={formStyles}
                        funcs={formFuncs}
                        customData={{
                            fieldsWrapper: css.fieldsWrapper,
                            customFormButton: {
                                wrapper: css.buttonsWrapper,
                                ok: {
                                    singleButton: css.button,
                                    butWrapper: css.buttonInner
                                },
                                cancel: {
                                    singleButton: css.button,
                                    butWrapper: css.buttonInner
                                },
                                isFullWidth: true
                            }
                        }}
                        okButtonLoader={true}
                    />
                </div>
            </div>
        </Portal>
    );
}
